import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import useCountryStore from '@/stores/countryStore'
import { Country } from '@/services/configService'
import DataTable from '@/components/ui/DataTable'
import FilterBar from '@/components/ui/FilterBar'
import { DetailHeader } from '@/components/ui/DetailHeader'
import { Badge } from '@/components/ui/badge'
import { Plus, Edit, Trash2, Eye } from 'lucide-react'

const CountryList = () => {
  const navigate = useNavigate()
  const {
    countries,
    loading,
    error,
    pagination,
    fetchCountries,
    setFilters,
    deleteCountry
  } = useCountryStore()

  const [search, setSearch] = useState('')
  const [continentFilter, setContinentFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)

  const continents = ['Europe', 'North America', 'South America', 'Asia', 'Africa', 'Oceania', 'Antarctica']

  useEffect(() => {
    loadCountries()
  }, [search, continentFilter, statusFilter, page, pageSize])

  const loadCountries = async () => {
    const filters: any = {}
    if (search) filters.search = search
    if (continentFilter !== 'all') filters.continent = continentFilter
    if (statusFilter !== 'all') filters.enabled = statusFilter === 'enabled'
    
    setFilters({
      ...filters,
      page,
      limit: pageSize
    })
    
    await fetchCountries()
  }

  const handleDelete = async (id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce pays ?')) {
      await deleteCountry(id)
      loadCountries()
    }
  }

  const handleView = (country: Country) => {
    navigate(`/countries/${country.id}`)
  }

  const handleEdit = (country: Country) => {
    navigate(`/countries/${country.id}/edit`)
  }

  const columns = [
    {
      key: 'flagEmoji',
      label: 'Drapeau',
      sortable: false,
      render: (value: string) => (
        <span className="text-2xl">{value || '🏳️'}</span>
      )
    },
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      render: (value: string) => (
        <span className="font-mono text-sm">{value}</span>
      )
    },
    {
      key: 'name',
      label: 'Nom',
      sortable: true,
      render: (value: string, row: Country) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          {row.native_name && (
            <div className="text-sm text-gray-500">{row.native_name}</div>
          )}
        </div>
      )
    },
    {
      key: 'defaultCurrencyId',
      label: 'Devise',
      sortable: false,
      render: (value: string) => (
        <div className="flex items-center gap-2">
          <span className="font-mono text-sm">
            {value || 'N/D'}
          </span>
        </div>
      )
    },
    {
      key: 'defaultLanguage',
      label: 'Langue',
      sortable: true,
      render: (value: string) => (
        <Badge variant="outline" className="bg-gray-100 text-gray-800">
          {value?.toUpperCase() || 'N/D'}
        </Badge>
      )
    },
    {
      key: 'continent',
      label: 'Continent',
      sortable: true,
      render: (value: string) => (
        <Badge variant="outline" className="bg-blue-100 text-blue-800">
          {value || 'N/D'}
        </Badge>
      )
    },
    {
      key: 'enabled',
      label: 'Statut',
      sortable: true,
      render: (value: boolean) => (
        <Badge variant={value ? 'default' : 'secondary'}>
          {value ? 'Activé' : 'Désactivé'}
        </Badge>
      )
    },
    {
      key: 'taxCalculation',
      label: 'Calcul TVA',
      sortable: true,
      render: (value: 'inclusive' | 'exclusive') => (
        <Badge variant="outline" className="bg-gray-100 text-gray-800">
          {value === 'inclusive' ? 'TTC' : 'HT'}
        </Badge>
      )
    }
  ]

  const actions = [
    {
      label: 'Voir',
      icon: <Eye className="h-4 w-4" />,
      onClick: handleView,
      variant: 'outline' as const
    },
    {
      label: 'Modifier',
      icon: <Edit className="h-4 w-4" />,
      onClick: handleEdit,
      variant: 'outline' as const
    },
    {
      label: 'Supprimer',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: (row: Country) => handleDelete(row.id),
      variant: 'destructive' as const
    }
  ]

  const filters = [
    {
      key: 'search',
      label: 'Recherche',
      type: 'text' as const,
      placeholder: 'Rechercher un pays...'
    },
    {
      key: 'continent',
      label: 'Continent',
      type: 'select' as const,
      options: [
        { value: 'all', label: 'Tous les continents' },
        ...continents.map(cont => ({ value: cont, label: cont }))
      ]
    },
    {
      key: 'status',
      label: 'Statut',
      type: 'select' as const,
      options: [
        { value: 'all', label: 'Tous les statuts' },
        { value: 'enabled', label: 'Activé' },
        { value: 'disabled', label: 'Désactivé' }
      ]
    }
  ]

  const handleFilterChange = (filters: any[]) => {
    filters.forEach(filter => {
      if (filter.key === 'search') {
        setSearch(filter.value)
        setPage(1)
      } else if (filter.key === 'continent') {
        setContinentFilter(filter.value)
        setPage(1)
      } else if (filter.key === 'status') {
        setStatusFilter(filter.value)
        setPage(1)
      }
    })
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize)
    setPage(1)
  }

  const breadcrumbs = [
    { label: 'Tableau de bord', href: '/' },
    { label: 'Pays' }
  ]

  const headerActions = [
    {
      label: 'Ajouter un pays',
      onClick: () => navigate('/countries/new'),
      variant: 'default' as const,
      icon: <Plus className="h-4 w-4" />
    }
  ]

  return (
    <div className="space-y-6">
      <DetailHeader
        title="Pays"
        subtitle="Gérez les pays disponibles dans votre système"
        breadcrumbs={breadcrumbs}
        actions={headerActions}
      />

      {error && (
        <div className="bg-red-50 text-red-600 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      <FilterBar
        filters={filters}
        onFilterChange={handleFilterChange}
        onSearch={(value) => {
          setSearch(value)
          setPage(1)
        }}
        searchPlaceholder="Rechercher un pays..."
      />

      <DataTable
        columns={columns}
        data={countries}
        actions={actions}
        loading={loading}
        pagination={{
          current: page,
          total: pagination.total,
          pageSize: pageSize,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange
        }}
        emptyMessage="Aucun pays trouvé"
      />
    </div>
  )
}

export default CountryList
